import { useCallback, useState } from "react";
import { Box, useTheme } from "@mui/material";

import type { RedoReasonRequest, UUID } from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import {
  Banner,
  BannerList,
  Button,
  Dropdown,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";

// Predefined reasons for cancelling a redo in progress. The selected labels are
// composed into the `reason` string sent to the existing cancelRedo endpoint —
// no new persistence is needed (it already stores reason + reasonDescription).
const cancelReasons: { value: string; label: string }[] = [
  { value: "started-in-error", label: "I started the redo in error" },
  { value: "no-longer-required", label: "The redo is no longer required" },
  { value: "original-correct", label: "The original form is correct" },
  { value: "another-form-instead", label: "Another form will be used instead" },
  { value: "other", label: "Other" },
];

/** API functions that can be injected for testing */
export interface CancelRedoDialogApi {
  cancelRedo: (
    formId: UUID,
    body: RedoReasonRequest,
  ) => Promise<{ status: number; data?: unknown }>;
}

const defaultApi: CancelRedoDialogApi = {
  cancelRedo: (formId, body) => api.forms.cancelRedo(formId, body),
};

/**
 * Shown to the author who is cancelling an in-progress redo so they must record
 * why (DEV-2432). Cancelling reverts the form to its completed state, restores
 * the links severed when the redo started, and discards the in-progress
 * replacement. Reachable from the original form's row ("Cancel redo") and the
 * replacement's row ("Abandon redo") — both cancel the same redo, so both route
 * through this dialog. `formId` is always the ORIGINAL form being redone.
 */
export function CancelRedoDialog({
  formId,
  onClose,
  onSuccess,
  successMessage = "Redo cancelled",
  apiOverride,
}: {
  formId: UUID;
  onClose: () => void;
  onSuccess: () => void;
  successMessage?: string;
  apiOverride?: CancelRedoDialogApi;
}) {
  const apiClient = apiOverride ?? defaultApi;
  const theme = useTheme();

  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [additionalInfo, setAdditionalInfo] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // At least one reason must be selected; the additional information is optional.
  const canContinue = !isSubmitting && !!selectedReasons.length;

  const cancel = useCallback(async () => {
    setIsSubmitting(true);
    const reason = cancelReasons
      .filter((r) => selectedReasons.includes(r.value))
      .map((r) => r.label)
      .join("; ");
    const trimmedInfo = additionalInfo.trim();
    const body: RedoReasonRequest = {
      reason,
      reasonDescription: trimmedInfo || undefined,
    };
    try {
      const result = await apiClient.cancelRedo(formId, body);
      if (result.status === 204) {
        renderSuccessToast({ message: successMessage });
        onSuccess();
      } else {
        // apiAxios uses validateStatus: status < 500, so 4xx responses resolve
        // rather than throw — surface the server-provided reason when present.
        const errorMessage = (result.data as { reason?: string })?.reason;
        throw new Error(errorMessage ?? "Unknown error");
      }
    } catch (e) {
      renderErrorToast({ message: (e as Error).message });
      setIsSubmitting(false);
    }
  }, [
    apiClient,
    formId,
    selectedReasons,
    additionalInfo,
    successMessage,
    onSuccess,
  ]);

  return (
    <PopupDialog open={true} onClose={onClose}>
      <PopupDialogTitle titleText="Cancel Redo" closeDialog={onClose} />
      <Box sx={{ mb: 2 }}>
        <Banner
          body={[
            "You are about to cancel the redo in progress for this form. The in-progress redo version will be deleted. The original form will remain accessible and will become active again for use in the admission. Users with access to the admission will be informed of your decision to cancel the redo along with your reasoning.",
          ]}
          bannerType={BannerList.WARNING}
        />
      </Box>
      <Dropdown
        label="Please select a reason why you are cancelling this redo"
        multiple
        name="cancelReason"
        values={cancelReasons}
        selectedValue={selectedReasons}
        onChange={(e: string[]) => {
          setSelectedReasons(e);
        }}
      />
      <Box sx={{ mt: 2 }}>
        <TextField
          name="additionalInfo"
          label="Please provide additional information"
          onChange={(e: string) => {
            setAdditionalInfo(e);
          }}
          value={additionalInfo}
          useFullWidth={true}
          multiline
          rows="5"
        />
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          marginTop: theme.spacing(3),
        }}
      >
        <Button label={"Close"} onClick={onClose} variant="outlined" />
        <Button
          color="primary"
          label={"Cancel Redo"}
          disabled={!canContinue}
          onClick={() => {
            void cancel();
          }}
        />
      </Box>
    </PopupDialog>
  );
}
